import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ButtonGroup
} from '@mui/material';
import { Visibility } from '@mui/icons-material';
import useManageLabel from './useManageLabel';
import useShipDate from '../setting/shipDate/useShipDate';
import { formatDate } from '../../utils/dateUtils';

const LabelGenerator = () => {
  const { 
    isLoading, 
    error, 
    generateLabelPDF 
  } = useManageLabel();
  
  const { 
    shipDates, 
    getShipDates 
  } = useShipDate();

  const [selectedShipDate, setSelectedShipDate] = useState('');
  const [startNo, setStartNo] = useState('');
  const [endNo, setEndNo] = useState('');
  const [airShipDates, setAirShipDates] = useState([]);

  useEffect(() => {
    getShipDates();
  }, []);

  useEffect(() => {
    if (shipDates && shipDates.length > 0) {
      // Filter only air ship dates (isAir === true)
      const airDates = shipDates.filter(date => date.isAir === true);
      setAirShipDates(airDates);
    }
  }, [shipDates]);

  const handleShipDateChange = (event) => {
    setSelectedShipDate(event.target.value);
  };

  const handleStartNoChange = (event) => {
    setStartNo(event.target.value);
  };

  const handleEndNoChange = (event) => {
    setEndNo(event.target.value);
  };

  const handleGeneratePDF = async () => {
    if (!selectedShipDate) {
      alert('กรุณาเลือกรอบเรือ');
      return;
    }

    if (!startNo || !endNo) {
      alert('กรุณาใส่หมายเลขเริ่มต้นและหมายเลขสิ้นสุด');
      return;
    }

    if (parseInt(startNo) > parseInt(endNo)) {
      alert('หมายเลขเริ่มต้นต้องน้อยกว่าหมายเลขสิ้นสุด');
      return;
    }

    try {
      const shipDateInfo = getSelectedShipDateInfo();
      await generateLabelPDF(selectedShipDate, startNo, endNo, shipDateInfo);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  const getSelectedShipDateInfo = () => {
    return airShipDates.find(date => date.id === selectedShipDate);
  };

  const selectedShipDateInfo = getSelectedShipDateInfo();

  return (
    <Box sx={{ padding: 3 }}>
      <Typography variant="h4" gutterBottom>
        สร้างป้าย QR Code สำหรับรอบเรือ
      </Typography>
      
      <Paper sx={{ padding: 3, marginTop: 2 }}>
        <Grid container spacing={3}>
          {/* Ship Date Selection */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="ship-date-label">รอบเรือ (เครื่องบิน)</InputLabel>
              <Select
                labelId="ship-date-label"
                id="ship-date-select"
                value={selectedShipDate}
                label="รอบเรือ (เครื่องบิน)"
                onChange={handleShipDateChange}
              >
                {airShipDates.map((date) => (
                  <MenuItem key={date.id} value={date.id}>
                    {formatDate(date.shipDate)} - เครื่องบิน
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Start Number */}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="หมายเลขเริ่มต้น"
              type="number"
              value={startNo}
              onChange={handleStartNoChange}
              inputProps={{ min: 1 }}
            />
          </Grid>

          {/* End Number */}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="หมายเลขสิ้นสุด"
              type="number"
              value={endNo}
              onChange={handleEndNoChange}
              inputProps={{ min: 1 }}
            />
          </Grid>

          {/* Generate Button */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, marginTop: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Visibility />}
                onClick={handleGeneratePDF}
                disabled={isLoading || !selectedShipDate || !startNo || !endNo}
                size="large"
              >
                {isLoading ? 'กำลังสร้าง PDF...' : 'ดูตัวอย่าง PDF'}
              </Button>
            </Box>
          </Grid>

          {/* Loading Indicator */}
          {isLoading && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">
                  กำลังสร้าง PDF... กรุณารอสักครู่
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Error Display */}
          {error && (
            <Grid item xs={12}>
              <Alert severity="error">
                {error}
              </Alert>
            </Grid>
          )}
        </Grid>
      </Paper>
    </Box>
  );
};

export default LabelGenerator;
